/**
 * 矩形元素组件（SVG 矢量渲染，支持圆角/阴影/虚线边框）。支持双击进入文字编辑模式。
 */
import { useState, useCallback } from 'react';
import type { RectangleElement as RectangleElementType } from '@/components/canvas/document/publicTypes';
import type { ElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../../group/containerTransform';
import { roundedRectPathData, resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import { blurRadius, elementFillCss, firstShadow, firstStroke } from '@/components/canvas/elements/model';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { ShapeTextEditor } from '../components/ShapeTextEditor';
import { resolveShapeTextStyle } from '../utils/shapeTextStyle';
import { createTextContentData } from '@/components/canvas/elements/text/factory';

export function RectangleElement({
  element,
  dragOffset = { dx: 0, dy: 0 },
  onSelect,
  onMouseDown,
}: ElementComponentProps<RectangleElementType>) {
  const { dx, dy } = dragOffset;
  const { updateElement, setEditingTableCellId } = useCanvas();
  const [isEditing, setIsEditing] = useState(false);

  const rect = element;
  const { width, height } = rect.size;
  const stroke = firstStroke(rect.appearance);
  const strokeW = stroke?.width ?? 0;
  const r = resolveCornerRadiiPx(rect);
  const shadowEffect = firstShadow(rect.appearance);
  const textStyle = resolveShapeTextStyle(element);

  const shadow = shadowEffect
    ? `drop-shadow(${shadowEffect.offsetX}px ${shadowEffect.offsetY}px ${shadowEffect.blur}px ${shadowEffect.color})`
    : undefined;

  const wrapperStyle: React.CSSProperties = {
    position: 'absolute',
    left: rect.layout.x + dx,
    top: rect.layout.y + dy,
    width,
    height,
    opacity: rect.appearance.opacity,
    transform: composeContainerTransform(rect),
    filter: appendBlurFilter(shadow, blurRadius(rect)),
    cursor: 'inherit',
    // 不设 overflow:hidden，让 SVG 描边的外半部分可正常显示（描边居中于路径）；
    // 文字层 ShapeTextEditor 内部已自带 overflow:hidden 裁剪文字。
    overflow: 'visible',
  };

  const svgStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    overflow: 'visible',
  };

  const pathD = roundedRectPathData(width, height, r);

  let dashArray: string | undefined;
  if (stroke?.style === 'dashed' && strokeW > 0) {
    dashArray = `${strokeW * 3} ${strokeW * 2}`;
  } else if (stroke?.style === 'dotted' && strokeW > 0) {
    dashArray = `${strokeW} ${strokeW}`;
  }

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleEditStart = useCallback(() => {
    setEditingTableCellId(element.id);
  }, [element.id, setEditingTableCellId]);

  const handleEditEnd = useCallback(() => {
    setIsEditing(false);
    setEditingTableCellId(null);
  }, [setEditingTableCellId]);

  const handleCommit = useCallback((text: string) => {
    updateElement(element.id, {
      data: {
        ...element.data,
        text: text ? createTextContentData({ content: text }) : undefined,
      },
    });
  }, [element.id, element.data, updateElement]);

  return (
    <div
      style={wrapperStyle}
      data-element-hit
      data-element-id={rect.id}
      onClick={(e) => {
        if (isEditing) return;
        e.stopPropagation();
        onSelect(e);
      }}
      onDoubleClick={handleDoubleClick}
      onMouseDown={(e) => {
        if (isEditing) return;
        onMouseDown?.(e, element);
      }}
    >
      <svg width={width} height={height} style={svgStyle}>
        <path
          d={pathD}
          fill={elementFillCss(rect) ?? '#ddd'}
          stroke={strokeW > 0 ? (stroke?.color ?? '#999') : 'none'}
          strokeWidth={strokeW}
          strokeDasharray={dashArray}
        />
      </svg>
      <ShapeTextEditor
        text={element.data.text?.content ?? ''}
        textStyle={textStyle}
        isEditing={isEditing}
        onCommit={handleCommit}
        onEditStart={handleEditStart}
        onEditEnd={handleEditEnd}
      />
    </div>
  );
}
